import {Loader2} from 'lucide-react'
import React, {useContext, useEffect, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {FamilyContext} from '../../Context/FamilyHeadContext'
import {API_URL, doApiGet} from '../../services/apiService'
import ResultsList from './ResultsList'
import ResultsTabs from './ResultsTabs'
import UnifiedSearch from './UnifiedSearch'

const ResultsPage = () => {
    const [searchParams] = useSearchParams()
    const [extendedResults, setExtendedResults] = useState({
        facets: {
            nameMatches: [],
            birthMatches: [],
            deathMatches: [],
            placeMatches: [],
            documentMatches: [],
        },
        cities: [],
    })
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [countdownSeconds, setCountdownSeconds] = useState(null)
    const {isMainSite, processPerson} = useContext(FamilyContext)
    const {t, i18n} = useTranslation()
    const [activeTab, setActiveTab] = useState('name')

    const autoNavigateTimer = useRef(null)
    const countdownInterval = useRef(null)
    const navigate = useNavigate()

    const query = searchParams.get('query')?.split('/')[0]
    const show = searchParams.get('show') ?? query

    // פונקציה לניקוי טיימרים
    const clearTimers = () => {
        if (autoNavigateTimer.current) {
            clearTimeout(autoNavigateTimer.current)
            autoNavigateTimer.current = null
        }

        if (countdownInterval.current) {
            clearInterval(countdownInterval.current)
            countdownInterval.current = null
        }

        setCountdownSeconds(null)
    }

    // בדיקת תוצאה יחידה ויצירת מעבר אוטומטי
    useEffect(() => {
        // נקה טיימרים קודמים
        clearTimers()

        // חישוב מספר התוצאות הכולל
        const totalResults = Object.values(extendedResults.facets)
            .reduce((sum, matches) => sum + matches.length, 0)

        // אם יש בדיוק תוצאה אחת ולא במצב טעינה ואין שגיאה
        if (totalResults === 1 && !loading && !error && query) {
            // מציאת התוצאה היחידה
            const singleResult = Object.values(extendedResults.facets)
                .find(matches => matches.length > 0)?.[0]

            if (singleResult) {
                // התחל ספירה לאחור מ-5
                setCountdownSeconds(5)

                // הפעל אינטרוול שיעדכן את הספירה לאחור כל שנייה
                countdownInterval.current = setInterval(() => {
                    setCountdownSeconds(prev => {
                        if (prev <= 1) {
                            clearInterval(countdownInterval.current)
                            countdownInterval.current = null
                            return 0
                        }
                        return prev - 1
                    })
                }, 1000)

                // הפעל טיימר למעבר אוטומטי לאחר 5 שניות
                autoNavigateTimer.current = setTimeout(() => {
                    navigate(singleResult.path)
                }, 5000)
            }
        }

        // ניקוי כאשר הקומפוננטה מתפרקת או כשהתוצאות משתנות
        return () => {
            clearTimers()
        }
    }, [extendedResults, loading, error, query, navigate])

    useEffect(() => {
        const fetchExtendedResults = async () => {
            if (!query) {
                setLoading(false)
                return
            }

            setLoading(true)
            setError('')
            // נקה טיימרים כשמתחיל חיפוש חדש
            clearTimers()

            try {
                const data = await doApiGet(
                    `${API_URL}/search/result?query=${encodeURIComponent(
                        query,
                    )}&family=${isMainSite}&lang=${i18n.language}`,
                )

                const processedData = {
                    facets: {
                        nameMatches: data.facets.nameMatches.map((person) =>
                            processPerson(person),
                        ),
                        birthMatches: data.facets.birthMatches.map((person) =>
                            processPerson(person),
                        ),
                        deathMatches: data.facets.deathMatches.map((person) =>
                            processPerson(person),
                        ),
                        placeMatches: data.facets.placeMatches.map((person) =>
                            processPerson(person),
                        ),
                        documentMatches: data.facets.documentMatches.map((person) =>
                            processPerson(person),
                        ),
                    },
                    cities: data.cities,
                }

                setExtendedResults(processedData)
            } catch (err) {
                console.error('Search error:', err)
                setError(t('searchResults.error'))
            } finally {
                setLoading(false)
            }
        }

        fetchExtendedResults()
    }, [query, isMainSite, processPerson, t])

    if (!query) {
        return (
            <div className="min-h-screen bg-[#f8f5f1] px-4 py-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-3xl font-bold text-[#4a3f30] mb-6">
                        {t('resultsPage.searchPlaceholder')}
                    </h1>
                    <UnifiedSearch variant="standalone"/>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-[#f8f5f1]">
            <div className="px-4 py-4 sm:py-8">
                <div className="max-w-4xl mx-auto">
                    <h1 className="text-2xl sm:text-3xl font-bold text-[#4a3f30] mb-4 sm:mb-6">
                        {`${t('resultsPage.title')} "${show}"`}
                        {countdownSeconds !== null && (
                            <div className="text-base sm:text-lg font-normal text-[#8B5E3C] mt-2">
                                {t('searchResults.autoRedirect', {seconds: countdownSeconds})}
                            </div>
                        )}
                    </h1>
                    <div className="mb-6 sm:mb-8">
                        <UnifiedSearch variant="standalone"/>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center py-8 sm:py-12">
                            <Loader2 className="w-8 h-8 text-[#8B5E3C] animate-spin"/>
                        </div>
                    ) : error ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-600">
                            {error}
                        </div>
                    ) : (
                        <>
                            <ResultsTabs
                                activeTab={activeTab}
                                setActiveTab={setActiveTab}
                                results={extendedResults}
                            />
                            <ResultsList
                                results={extendedResults.facets[`${activeTab}Matches`]}
                                query={query}
                            />
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default ResultsPage